/* eslint-disable camelcase */
import useSWR from 'swr';
import useSWRInfinite from 'swr/infinite';
import fetcher from '../../utils/fetcher';
import type { BnplMovementsHttpBody } from '../../types/bnpl';

export type BnplPurchasingPowerPayload = {
  availableBalance?: number;
  currentDebt?: number;
  contractMaxAmount?: number;
  preApproveMaxAmount?: number;
  totalHistoricalSpent?: number;
  totalHistoricalPaid?: number;
};

export type BnplPurchasingPowerResponse = {
  data: BnplPurchasingPowerPayload;
  status: number;
};

export function getBnplPurchasingPower(token: string | null) {
  const {
    data,
    error,
    isLoading,
    mutate,
  } = useSWR(
    token
      ? [`${process.env.REACT_APP_API_URL}/bnpl/purchasing-power`, 'GET', null, token]
      : null,
    ([url, method, body]) => fetcher(url, method, body, token),
  );

  const purchasingPowerData = (data as BnplPurchasingPowerResponse | undefined)?.data ?? null;

  return {
    purchasingPowerData,
    purchasingPowerLoading: isLoading,
    purchasingPowerError: error,
    mutate,
  };
}

const BNPL_MOVEMENTS_PAGE_SIZE = 20;

type BnplMovementsPageResponse = BnplMovementsHttpBody & { status: number };

export function getBnplMovements(
  token: string | null,
  type?: 'FINANCIAL' | 'NON_FINANCIAL',
  pageSize = BNPL_MOVEMENTS_PAGE_SIZE,
) {
  const getKey = (
    _pageIndex: number,
    previousPageData: BnplMovementsPageResponse | null,
  ) => {
    if (!token) return null;
    if (previousPageData) {
      const { offset, data, total } = previousPageData;
      const loaded = offset + data.length;
      if (loaded >= total || data.length === 0) return null;
    }
    const offset = previousPageData
      ? previousPageData.offset + previousPageData.data.length
      : 0;
    const url = `${process.env.REACT_APP_API_URL}/bnpl/movements?limit=${String(pageSize)}&offset=${String(offset)}${type ? `&type=${type}` : ''}`;
    return [url, 'GET', null, token] as const;
  };

  const {
    data: pages,
    error,
    isLoading,
    isValidating,
    setSize,
    mutate,
  } = useSWRInfinite(
    getKey,
    ([url, method, body, t]) => fetcher(url, method, body, t),
  );

  const normalizedPages = (pages ?? []) as BnplMovementsPageResponse[];
  const items = normalizedPages.flatMap((p) => p.data);
  const lastPage = normalizedPages[normalizedPages.length - 1];
  const total = lastPage?.total ?? normalizedPages[0]?.total ?? 0;
  const hasMore = Boolean(
    lastPage
      && lastPage.offset + lastPage.data.length < lastPage.total
      && lastPage.data.length > 0,
  );

  const loadMore = () => {
    if (hasMore && !isValidating) {
      setSize((s) => s + 1);
    }
  };

  const refresh = () => {
    setSize(1);
    return mutate();
  };

  return {
    movementItems: items,
    movementsTotal: total,
    movementsLoading: Boolean(isLoading && normalizedPages.length === 0),
    movementsLoadingMore: Boolean(isValidating && normalizedPages.length > 0),
    movementsError: error,
    movementsHasMore: hasMore,
    movementsLoadMore: loadMore,
    movementsRefresh: refresh,
  };
}

export type BNPLRegisterPreApproveResponse = {
    data:{
    accepted: boolean,
    maxApprovedAmount: number,
    widgetToken: string,
    isRegistered: boolean,
    identificationId: string,
    publicKey: string,
    hardFilters: undefined | { [key: string]: string },
    },
};

export default async function postBNPLRegisterPreApprove(body: { idNumber: string,
    email: string,
    captchaToken: string,
    origin?: string }, token: string): Promise<BNPLRegisterPreApproveResponse> {
  const response = await fetcher(
    `${process.env.REACT_APP_API_URL}/pre-loan-request/bnpl/register/pre-approve`,
    'POST',
    body,
    token,
  );
  return response;
}
